import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card } from "@/components/ui/card";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const PROVINCIAS = ["Cáceres", "Badajoz", "Salamanca", "Ávila", "Toledo", "Madrid", "Otra"];

export default function Register() {
  const { register } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [form, setForm] = useState({
    name: "", empresa: "", dni_cif: "", direccion: "", telefono: "",
    email: "", password: "", provincia: "Cáceres", role: "productor",
  });

  const update = (k, v) => setForm((s) => ({ ...s, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    if (form.password.length < 6) { toast.error("La contraseña debe tener al menos 6 caracteres"); return; }
    setBusy(true);
    const r = await register(form);
    setBusy(false);
    if (!r.ok) { toast.error(r.error); return; }
    toast.success("Cuenta creada correctamente");
    navigate("/productor");
  };

  return (
    <div className="min-h-screen paprika-bg flex items-center justify-center p-4 md:p-8">
      <Card className="w-full max-w-2xl p-8 md:p-10 rounded-2xl shadow-xl border-stone-200">
        <div className="flex items-center gap-3 mb-6">
          <img src="/logo-pimenton.png" alt="DOP" className="h-12 w-12 object-contain" />
          <div className="font-heading font-semibold text-stone-900">DOP Pimentón de la Vera</div>
        </div>
        <h2 className="text-3xl font-heading font-bold text-stone-900">Registro de Productor / Industria</h2>
        <p className="mt-2 text-stone-600">Completa tus datos para acceder al sistema documental.</p>

        <form onSubmit={submit} className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="md:col-span-2">
            <Label>Tipo de cuenta</Label>
            <Select value={form.role} onValueChange={(v) => update("role", v)}>
              <SelectTrigger data-testid="select-tipo-cuenta" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="productor">Productor agrícola</SelectItem>
                <SelectItem value="industria">Industria transformadora</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Nombre completo *</Label>
            <Input required value={form.name} onChange={(e) => update("name", e.target.value)} data-testid="input-name" className="mt-1.5" />
          </div>
          <div>
            <Label>Empresa</Label>
            <Input value={form.empresa} onChange={(e) => update("empresa", e.target.value)} data-testid="input-empresa" className="mt-1.5" />
          </div>
          <div>
            <Label>DNI / CIF *</Label>
            <Input required value={form.dni_cif} onChange={(e) => update("dni_cif", e.target.value)} data-testid="input-dni" className="mt-1.5" />
          </div>
          <div>
            <Label>Teléfono</Label>
            <Input value={form.telefono} onChange={(e) => update("telefono", e.target.value)} data-testid="input-telefono" className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label>Dirección</Label>
            <Input value={form.direccion} onChange={(e) => update("direccion", e.target.value)} data-testid="input-direccion" className="mt-1.5" />
          </div>
          <div>
            <Label>Provincia</Label>
            <Select value={form.provincia} onValueChange={(v) => update("provincia", v)}>
              <SelectTrigger data-testid="select-provincia" className="mt-1.5"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PROVINCIAS.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>Email *</Label>
            <Input type="email" required value={form.email} onChange={(e) => update("email", e.target.value)} data-testid="input-email" className="mt-1.5" />
          </div>
          <div className="md:col-span-2">
            <Label>Contraseña *</Label>
            <Input type="password" required minLength={6} value={form.password} onChange={(e) => update("password", e.target.value)} data-testid="input-password" className="mt-1.5" />
            <p className="text-xs text-stone-500 mt-1">Mínimo 6 caracteres</p>
          </div>
          <div className="md:col-span-2">
            <Button type="submit" disabled={busy} className="w-full h-11 bg-primary hover:bg-primary-hover text-white" data-testid="btn-register-submit">
              {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Crear cuenta"}
            </Button>
          </div>
        </form>
        <div className="mt-6 text-center text-sm text-stone-600">
          ¿Ya tienes cuenta? <Link to="/login" className="text-primary font-medium hover:underline">Entrar</Link>
        </div>
      </Card>
    </div>
  );
}
